<template>
  <BaseModal :modalTitle="$t('W: Opret tillægsydelse')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <UiInputTextFieldVee
        name="name"
        :label="$t('term.name')"
        :placeholder="$t('form.placeholder.name')"
        :rules="['required']"
        isRequired
        :unit="currentLocale.toUpperCase()"
        v-model="form.name" />

      <div class="mt-4">
        <UiInputSelectVee
          name="scope"
          :label="$t('W: Anvendelse')"
          :options="scopeOptions"
          :rules="['required']"
          isRequired
          v-model="form.scope" />
      </div>

      <div
        v-if="form.scope === 'per-analysis'"
        class="mt-4">
        <UiComboboxMultiVee
          name="applicableAnalysisIDs"
          :label="$t('W: Gældende analyser')"
          :options="analysisOptions"
          :placeholder="$t('term.search')"
          v-model="form.applicableAnalysisIDs" />
      </div>

      <template #buttons>
        <UiBaseButton
          :text="$t('action.create')"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import type { TAddonServiceScope } from '@team-milva/lab-models/resources'
import { useAddonServiceStore } from '@/stores/addonServiceStore'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useAppStore } from '@/stores/appStore'
import { useModalStore } from '@/stores/modalStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { mapActions, mapState } from 'pinia'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

export default {
  name: 'CreateAddonServiceModal',

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: {
        name: '',
        scope: 'per-order' as TAddonServiceScope,
        applicableAnalysisIDs: [] as string[],
      },
      isLoading: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAnalysisStore, ['allAnalyses']),
    ...mapState(useAppStore, ['currentLocale', 'defaultMessageSaveIconTime']),

    scopeOptions(): { value: TAddonServiceScope; text: string }[] {
      return [
        { value: 'per-order', text: this.$t('W: Pr. ordre') },
        { value: 'per-analysis', text: this.$t('W: Pr. analyse') },
      ]
    },

    analysisOptions(): { text: string; value: string }[] {
      return [...this.allAnalyses]
        .map((analysis) => ({
          text: analysis.getName(this.currentLocale),
          value: analysis.id,
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },
  },

  methods: {
    ...mapActions(useAddonServiceStore, ['createAddonService']),
    ...mapActions(useModalStore, ['closeTop']),

    async onSubmit() {
      this.isLoading = true
      this.errorText = ''

      try {
        await this.createAddonService({
          name: this.form.name,
          scope: this.form.scope,
          applicableAnalysisIDs:
            this.form.scope === 'per-analysis'
              ? this.form.applicableAnalysisIDs
              : [],
        })
        useToast().success(this.$t('W: Tillægsydelse oprettet'))
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        this.closeTop()
      } catch {
        useToast().error(this.$t('W: Kunne ikke oprette tillægsydelse'))
        this.errorText = this.$t('error.common.standard')
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
